import { removeEffectFamily, removeConcentrationEffects, replaceCasterConcentration } from '../../../shared/rules/effects/effectLifecycle.mjs';
import { getEffectiveWalkSpeed } from './movementCore.mjs';
import { conditionDescription } from './conditionDescriptions';
import { getCanonicalCombatantIdentity, createCombatState, eventFor, resolveCombatEvent, emit, expandUndoStack, compactUndoStack, advanceTurn } from '../../../shared/rules/combat/turnTimeline.mjs';

export { getCanonicalCombatantIdentity, createCombatState, resolveCombatEvent, compactUndoStack, advanceTurn };

const BOUNDARIES = new Set(['start', 'end']);
const STACK_POLICIES = new Set(['replace', 'refresh', 'stack', 'ignore']);

export const durationToExpiration = (duration, { sourceCombatantId, targetCombatantId, round = 1 } = {}) => {
  switch (duration?.type) {
    case 'untilSourceTurn':
    case 'sourceTurns':
      return { type: 'sourceTurn', combatantId: sourceCombatantId, boundary: duration.boundary, remainingOccurrences: duration.count || 1 };
    case 'untilTargetTurn':
    case 'targetTurns':
      return { type: 'targetTurn', combatantId: targetCombatantId, boundary: duration.boundary, remainingOccurrences: duration.count || 1 };
    case 'rounds':
      return { type: 'rounds', expiresAtRound: round + Math.max(1, duration.count || 1), boundary: duration.boundary };
    case 'combat': return { type: 'combatEnd' };
    case 'concentration': return { type: 'concentration', concentrationId: duration.concentrationId };
    case 'permanent': return { type: 'manual' };
    default: throw new Error(`Unsupported effect duration: ${duration?.type || 'missing'}`);
  }
};

/** Return the number of authoritative combat rounds before a round effect's
 * expiration boundary. Null means the effect is not a valid round duration. */
export const getRemainingRounds = (effect, combat) => {
  if (effect?.expiration?.type === 'elapsedRounds') return Math.max(0, effect.expiration.remainingRounds || 0);
  const expiresAtRound = effect?.expiration?.expiresAtRound;
  const currentRound = combat?.round;
  if (effect?.expiration?.type !== 'rounds' || !Number.isInteger(expiresAtRound) ||
    !Number.isInteger(currentRound)) return null;
  return Math.max(0, expiresAtRound - currentRound);
};


export const applyActiveEffect = (inputState, effect) => {
  const state = createCombatState(inputState);
  if (!effect?.id || !effect.targetCombatantId || !effect.expiration) throw new Error('Active effects require id, targetCombatantId, and expiration');
  if ((effect.expiration.type === 'sourceTurn' || effect.expiration.type === 'targetTurn') && (!BOUNDARIES.has(effect.expiration.boundary) || !effect.expiration.combatantId)) throw new Error('Turn expiration requires a combatant and boundary');
  const normalized = { ...effect, appliedAtRound: state.round, appliedAtTurnSequence: state.turnSequence, stackPolicy: STACK_POLICIES.has(effect.stackPolicy) ? effect.stackPolicy : 'refresh' };
  const match = normalized.stackKey && state.activeEffects.findIndex((item) => item.stackKey === normalized.stackKey);
  if (match >= 0 && normalized.stackPolicy !== 'stack') {
    if (normalized.stackPolicy === 'ignore') return state;
    const next = [...state.activeEffects];
    next[match] = normalized.stackPolicy === 'refresh' ? { ...next[match], ...normalized, id: next[match].id } : normalized;
    return { ...state, activeEffects: next };
  }
  return { ...state, activeEffects: [...state.activeEffects, normalized] };
};

export const removeActiveEffect = (inputState, effectId) => {
  const state = createCombatState(inputState);
  return { ...state, activeEffects: removeEffectFamily(state.activeEffects, [effectId]) };
};

/** Resolve repeat-save descriptors before initiative advances. The injected
 * resolver owns DiceBox/persistence and can return a replayed result. */
export const processEndOfTurnRepeatSaves = async (inputState, combatantId, resolveSave) => {
  let state = createCombatState(inputState);
  const canonicalCombatantId = getCanonicalCombatantIdentity(combatantId);
  const effects = state.activeEffects.filter((effect) => getCanonicalCombatantIdentity(effect) === canonicalCombatantId &&
    effect?.repeatSave?.timing === 'end-of-target-turn');
  const results = [];
  for (const effect of effects.sort((a, b) => String(a.id).localeCompare(String(b.id)))) {
    if (typeof resolveSave !== 'function') throw new Error('Recurring saves require an authoritative resolver.');
    const requestId = `repeat-save:${effect.id}:${canonicalCombatantId}:${state.turnSequence}`;
    const result = await resolveSave({
      requestId, replayIdentity: requestId, effectId: effect.id, sourceCombatantId: effect.sourceCombatantId,
      targetCombatantId: canonicalCombatantId, ability: effect.repeatSave.ability, dc: effect.repeatSave.dc,
      turnSequence: state.turnSequence, conditionId: effect.definitionId,
    });
    if (!result || !['resolved', 'replayed'].includes(result.status) || typeof result.success !== 'boolean') {
      throw new Error('Recurring save resolver returned an invalid result.');
    }
    results.push(result);
    if (result.combat) state = createCombatState(result.combat);
    // A tally (Flesh to Stone) ends on the server's count, already in `result.combat`.
    if (result.success && effect.repeatSave.onSuccess === 'remove-effect' && !effect.repeatSave.failuresNeeded) state = removeActiveEffect(state, effect.id);
  }
  return { state, results };
};

/** The single forward-turn gateway. Required saves are resolved and persisted
 * before initiative can move; a missing resolver therefore fails closed. */
export const requestEndTurn = async ({ combat, currentCombatant, nextCombatant, resolveRecurringSave }) => {
  const currentCombatantId = getCanonicalCombatantIdentity(currentCombatant);
  if (!currentCombatantId) return { state: advanceTurn(combat, nextCombatant ? { nextCombatantId: getCanonicalCombatantIdentity(nextCombatant) } : {}), results: [] };
  const processed = await processEndOfTurnRepeatSaves(combat, currentCombatantId, resolveRecurringSave);
  return {
    ...processed,
    state: advanceTurn(processed.state, nextCombatant ? { nextCombatantId: getCanonicalCombatantIdentity(nextCombatant) } : {}),
  };
};

export const hasActiveEffect = (combatState, combatantId, definitionId) =>
  Boolean(combatantId && definitionId && (combatState?.activeEffects || []).some((effect) =>
    effect?.targetCombatantId === combatantId && effect?.definitionId === definitionId));

export const removeCombatant = (inputState, combatantId) => {
  const state = createCombatState(inputState);
  const activeId = state.participants[state.activeTurn]?.characterId;
  const participants = state.participants.filter((item) => item.characterId !== combatantId);
  const activeTurn = activeId === combatantId ? null : participants.findIndex((item) => item.characterId === activeId);
  return { ...state, participants, activeTurn: activeTurn < 0 ? null : activeTurn, activeEffects: state.activeEffects.filter((effect) => effect.targetCombatantId !== combatantId && (effect.removeOnSourceRemoval === false || effect.sourceCombatantId !== combatantId) && effect.expiration?.combatantId !== combatantId), pendingDecisions: state.pendingDecisions.filter((item) => item.sourceCombatantId !== combatantId && item.targetCombatantId !== combatantId) };
};


export const undoTurn = (inputState) => {
  const state = createCombatState(inputState);
  if (!state.undoStack.length) return state;
  const snapshot = expandUndoStack(state.undoStack).at(-1);
  const activeId=snapshot.participants?.[snapshot.activeTurn]?.characterId;
  const ordering=state.orderRevision ? {participants:state.participants,activeTurn:activeId?state.participants.findIndex(p=>p.characterId===activeId):null,manualOrder:state.manualOrder,orderRevision:state.orderRevision} : {};
  return createCombatState({ ...snapshot,...ordering,...(state.initiativeRevision!==undefined?{initiativeRevision:state.initiativeRevision}:{}), undoStack: state.undoStack.slice(0, -1) });
};

export const endCombat = (inputState) => {
  let state = createCombatState(inputState);
  state = emit(state, eventFor('combatEnded', state));
  return {
    ...state,
    activeTurn: null,
    activeEffects: state.activeEffects.filter((effect) => effect.definitionId !== 'brutal-strike-pending'),
    pendingDecisions: [],
    turnFeatureUsage: {},
  };
};

export const beginConcentration = (inputState, rootEffect) => {
  const state = createCombatState(inputState);
  return { ...state, activeEffects: replaceCasterConcentration(state.activeEffects, rootEffect) };
};

export const endConcentration = (inputState, concentrationId, sourceCombatantId) => {
  const state = createCombatState(inputState);
  return { ...state, activeEffects: removeConcentrationEffects(state.activeEffects, concentrationId, sourceCombatantId) };
};

export const getEffectiveSpeed = (baseSpeed, effects, targetCombatantId) => {
  return getEffectiveWalkSpeed(baseSpeed, effects, targetCombatantId);
};

/** Apply the shared speed pipeline without changing the persisted movement block. */
export const getEffectiveMovement = (baseMovement, combatState, targetCombatantId) => {
  const apply = (value) => {
    if (typeof value === 'number') return getEffectiveSpeed(value, combatState, targetCombatantId);
    if (typeof value === 'string') {
      if (/\d\s*(?:ft\.?|feet)/i.test(value)) return value.replace(/-?\d+(?:\.\d+)?(?=\s*(?:ft\.?|feet))/ig, number => String(getEffectiveSpeed(Number(number), combatState, targetCombatantId)));
      const match = value.match(/-?\d+(?:\.\d+)?/);
      if (!match) return value;
      return value.replace(match[0], String(getEffectiveSpeed(Number(match[0]), combatState, targetCombatantId)));
    }
    return value;
  };
  if (Array.isArray(baseMovement)) return baseMovement.map((mode) => ({ ...mode, value: apply(mode?.value ?? mode?.speed ?? mode?.distance) }));
  if (baseMovement && typeof baseMovement === 'object') {
    return Object.fromEntries(Object.entries(baseMovement).map(([mode, value]) => [mode, apply(value)]));
  }
  return apply(baseMovement);
};

const INTERNAL_EFFECTS = new Set(['brutal-strike-pending', 'brutal-strike-choice-pending', 'brutal-strike-used']);

/** The single condition selector used by character and enemy UIs. */
export const getActiveConditionsForCombatant = (combatantId, combatState, resolveCombatantName = () => null) =>
  (combatState?.activeEffects || [])
    .filter((effect) => effect?.targetCombatantId === combatantId && effect.userVisible !== false && !INTERNAL_EFFECTS.has(effect.definitionId))
    .map((effect) => {
      const sourceName = effect.sourceCombatantId ? resolveCombatantName(effect.sourceCombatantId) : null;
      const expiration = effect.expiration || {};
      const duration = expiration.type === 'sourceTurn' && expiration.boundary === 'start'
        ? `Until the start of ${sourceName || 'the source'}'s next turn`
        : expiration.type === 'elapsedRounds' ? `${Math.ceil(expiration.remainingRounds / 10)} minutes of game time` : effect.duration;
      return {
        ...effect,
        icon: effect.icon || '✦',
        source: sourceName ? `Source: ${sourceName}` : effect.source,
        duration,
        remainingRounds: getRemainingRounds(effect, combatState),
        description: effect.description || (effect.definitionId === 'hamstring-blow'
          ? `Speed reduced by 15 feet until the start of ${sourceName || 'the source'}'s next turn.`
          : conditionDescription(effect)),
      };
    });

